/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// Browser-side Snowflake KEYPAIR_JWT auth.
///
/// Snowflake's REST API accepts JWTs signed by the user's RSA private
/// key as a `Bearer` token (with `X-Snowflake-Authorization-Token-Type:
/// KEYPAIR_JWT`). We sign these in the browser via WebCrypto so
/// keypair-auth users can drive `useSnowflake` without a server
/// fallback. Mirrors `snowflake-connector-python`'s `auth_keypair.py`
/// logic, just running in JS instead of Python.
///
/// JWT shape per Snowflake's docs:
///   Header: { alg: 'RS256', typ: 'JWT' }
///   Claims:
///     iss = `<UPPER_ACCOUNT>.<UPPER_USER>.SHA256:<base64_fingerprint>`
///     sub = `<UPPER_ACCOUNT>.<UPPER_USER>`
///     iat = now (seconds)
///     exp = now + 3600 (max 1 hour per Snowflake)
///
///   Account = the bare account locator from `select current_account()`,
///     uppercased. We strip any `.region` suffix from the legacy
///     locator format; for the modern `orgname-accountname` form, we
///     keep the full hyphenated name uppercased.
///   Fingerprint = SHA-256 of the SPKI DER of the matching public
///     key, base64-encoded.
///
/// We can't extract the public key directly from a CryptoKey created
/// from PKCS#8 — WebCrypto doesn't expose the modulus + exponent on a
/// private key. The trick: re-export the private key as JWK (which
/// includes `n` + `e`), drop the private fields, re-import as a public
/// key, then export as SPKI. Costs nothing real and avoids a manual
/// ASN.1 build.
///
/// UPDATE: we now build the SPKI bytes manually via
/// `buildCanonicalRsaSpkiFromJwk` rather than round-tripping through
/// WebCrypto's `exportKey('spki')`. The browser's SPKI output varied
/// just enough between implementations to produce mismatched
/// fingerprints versus what OpenSSL / snowsql / Python compute,
/// surfacing as "JWT token is invalid" when users authenticated with
/// the same private key from snowsql. The canonical builder lives in
/// `~/scripts/crypto/rsaKeypair.ts` and is byte-identical to
/// `openssl rsa -pubout -outform DER`.
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import { buildCanonicalRsaSpkiFromJwk } from '~/scripts/crypto/rsaKeypair'

export interface SignedKeypairJwt {
  /** The compact JWT string. Use as the value of an `Authorization: Bearer` header. */
  jwt: string
  /** Unix-second expiration. Caller can cache the JWT until this minus a buffer. */
  expiresAt: number
}

/**
 * Build a Snowflake KEYPAIR_JWT from a stored PEM private key. This
 * is a pure compute step — no network. Use the returned `expiresAt`
 * to decide when to re-mint.
 */
export async function signKeypairJwt(args: {
  account: string  // sfId as stored on the account doc — e.g. `xy12345` or `myorg-account`
  user: string     // Snowflake user
  privateKeyPem: string
  /** JWT lifetime in seconds. Snowflake caps at 3600; default 3540 (~59 min) for a small refresh buffer. */
  ttlSeconds?: number
}): Promise<SignedKeypairJwt> {
  if (typeof window === 'undefined' || !window.crypto?.subtle) {
    throw new Error('WebCrypto is not available in this browser.')
  }

  const accountForJwt = canonicalAccountForJwt(args.account)
  const userForJwt = (args.user || '').trim().toUpperCase()
  if (!accountForJwt) throw new Error('Snowflake account identifier is required.')
  if (!userForJwt) throw new Error('Snowflake username is required.')

  const privateKey = await importPrivateKeyPem(args.privateKeyPem)
  const fingerprint = await derivePublicKeyFingerprint(privateKey)

  const now = Math.floor(Date.now() / 1000)
  const ttl = Math.min(Math.max(args.ttlSeconds ?? 3540, 60), 3600)
  const exp = now + ttl

  const header = { alg: 'RS256', typ: 'JWT' }
  const claims = {
    iss: `${accountForJwt}.${userForJwt}.SHA256:${fingerprint}`,
    sub: `${accountForJwt}.${userForJwt}`,
    iat: now,
    exp,
  }

  const signingInput = `${b64urlJson(header)}.${b64urlJson(claims)}`
  const signature = await window.crypto.subtle.sign(
    { name: 'RSASSA-PKCS1-v1_5' },
    privateKey,
    new TextEncoder().encode(signingInput),
  )
  const jwt = `${signingInput}.${b64urlBytes(new Uint8Array(signature))}`
  return { jwt, expiresAt: exp }
}

/**
 * Import a PEM-formatted PKCS#8 private key as a `CryptoKey`. The key
 * is marked extractable so we can re-export it as JWK to derive the
 * SPKI for fingerprinting (see `derivePublicKeyFingerprint`). The
 * caller can keep the resulting key in memory for the session — we
 * never round-trip the cleartext PEM after this point.
 */
export async function importPrivateKeyPem(pem: string): Promise<CryptoKey> {
  const der = pemBodyToDer(pem)
  return await window.crypto.subtle.importKey(
    'pkcs8',
    der,
    { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
    /* extractable */ true,
    ['sign'],
  )
}

// -----------------------------------------------------------------
// Internals
// -----------------------------------------------------------------

/**
 * Snowflake's JWT account portion: uppercase locator, stripped of any
 * `.region` suffix. The hyphen vs. underscore quirk DOES NOT apply
 * here — that's a URL-only concern. `myorg_account` and `myorg-account`
 * both go in as-is (uppercased) to the JWT.
 */
function canonicalAccountForJwt(sfId: string): string {
  if (!sfId) return ''
  const beforeDot = sfId.split('.')[0] || ''
  return beforeDot.trim().toUpperCase()
}

function pemBodyToDer(pem: string): ArrayBuffer {
  const stripped = pem
    .replace(/-----BEGIN [^-]+-----/g, '')
    .replace(/-----END [^-]+-----/g, '')
    .replace(/\s+/g, '')
  const binary = atob(stripped)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

async function derivePublicKeyFingerprint(privateKey: CryptoKey): Promise<string> {
  // Get the RSA public components from the private key's JWK. Then
  // build the canonical SPKI DER ourselves rather than trusting the
  // browser's `exportKey('spki')` output — historically, WebCrypto
  // implementations have varied on optional encoding details (most
  // commonly the explicit `NULL` AlgorithmIdentifier parameters byte),
  // and any byte-level variance changes the SHA-256 fingerprint that
  // ends up in the JWT's `iss` claim. The canonical builder produces
  // byte-identical output to `openssl rsa -pubout -outform DER`, which
  // is the form Snowflake hashes when verifying the JWT. Keeping
  // generation and signing on the same canonical bytes is the whole
  // reason `setRsaPublicKey` and this signer share `rsaKeypair.ts`.
  const jwk = await window.crypto.subtle.exportKey('jwk', privateKey)
  if (!jwk || jwk.kty !== 'RSA' || !jwk.n || !jwk.e) {
    throw new Error('Could not derive RSA public components from the stored private key.')
  }
  const spki = buildCanonicalRsaSpkiFromJwk(jwk)
  const hash = await window.crypto.subtle.digest('SHA-256', spki.buffer as ArrayBuffer)
  // Snowflake expects the fingerprint as standard base64 (with `+/=`),
  // not URL-safe base64.
  return bytesToBase64(new Uint8Array(hash))
}

function b64urlJson(obj: object): string {
  const json = JSON.stringify(obj)
  const bytes = new TextEncoder().encode(json)
  return b64urlBytes(bytes)
}

function b64urlBytes(bytes: Uint8Array): string {
  return bytesToBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function bytesToBase64(bytes: Uint8Array): string {
  // Chunked to dodge the call-stack limit on bigger payloads.
  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.byteLength; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)) as any)
  }
  return btoa(binary)
}
